/**
 * `usePermission` — reactive permission gate for UI rendering.
 *
 * Returns `true` when the authenticated user's permissions array (as stored
 * in `state.auth.permissions` by {@link authSlice}) contains the supplied
 * free-form permission string.
 *
 * Defensive default: when `state.auth.isAuthenticated === false`, this hook
 * returns `false` regardless of any permissions that may still be present in
 * state (e.g. rehydrated from localStorage before a heartbeat has confirmed
 * the session). UI-level gates therefore default to the least-privileged
 * outcome until the session is authoritatively live.
 *
 * The selector returns a primitive boolean so consumers only re-render on
 * actual true/false transitions — adding/removing unrelated permissions does
 * not trigger a render.
 *
 * This is a UX-only check. Authoritative enforcement happens server-side;
 * see spec §5.3.8 ("frontend checks are UX only, not security").
 *
 * SH-AUTH-004.
 */
import { useAppSelector } from '@dfq/shared/stores/hooks';

export function usePermission(permission: string): boolean {
  return useAppSelector((state) => {
    if (!state.auth.isAuthenticated) return false;
    return state.auth.permissions.includes(permission);
  });
}
